// Pure helpers for the waste screens (Stage 2C). No React, no API.
import type { WasteReason } from './wasteTypes'
import { WT } from './wasteStrings'

export const WASTE_REASONS: WasteReason[] = ['expired', 'spoiled', 'burnt', 'damaged', 'leftover', 'returned', 'other']

export interface LotForEstimate {
  id: string
  quantity_remaining: number
  unit_cost?: number | null
}

/**
 * The value the server will record, worked out the same way: the chosen batch
 * first, then the batches in the order given (earliest expiry first), each at
 * its own cost; anything past them at the average cost. Rounded to rupees'
 * two decimals.
 */
export function estimateWasteValue(qty: number, lots: LotForEstimate[], batchId: string | null, averageCost: number | null): number {
  if (!(qty > 0)) return 0
  let remaining = qty
  let value = 0
  const order = batchId ? [...lots.filter((l) => l.id === batchId), ...lots.filter((l) => l.id !== batchId)] : lots
  for (const l of order) {
    if (remaining <= 1e-9) break
    const take = Math.min(l.quantity_remaining, remaining)
    value += take * (l.unit_cost ?? averageCost ?? 0)
    remaining -= take
  }
  if (remaining > 1e-9) value += remaining * (averageCost ?? 0)
  return Math.round(value * 100) / 100
}

/** What stops the form from being sent, or null when it is ready. */
export function wasteFormProblem(f: { itemId: string | null; qty: number; reason: WasteReason | null; note: string; pin: string }): string | null {
  if (!f.itemId) return 'item'
  if (!(f.qty > 0)) return 'qty'
  if (!f.reason) return 'reason'
  if (f.reason === 'other' && !f.note.trim()) return 'note'
  if (!f.pin.trim()) return 'pin'
  return null
}

/** "expired 2 days ago" / "expires in 3 days" / null for no expiry. */
export function expiryWords(days: number | null | undefined, w: { expiresIn: (d: number) => string; expiredAgo: (d: number) => string }): string | null {
  if (days == null) return null
  return days < 0 ? w.expiredAgo(-days) : w.expiresIn(days)
}

/** The small chip on a waste-log line saying where it came from (none for a
 *  hand-recorded one). Batch 6 adds a failed batch ("cooking"); a void or
 *  refund no longer reads as "expiry". */
export function wasteSourceChip(source: string): string | null {
  switch (source) {
    case 'wrap_up':
      return WT.fromWrapUp
    case 'expiry':
      return WT.fromExpiry
    case 'batch':
      return WT.fromBatch
    default:
      return null
  }
}
